import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  Banner,
  Button,
  DataTable,
  EmptyStateSpeedGraphic,
  LoadingOrbit,
  Page,
  PageHeader,
  TagBadge,
  Text,
} from '@chatsdet/ui';
import { Robot } from '@phosphor-icons/react';
import { BRAND } from '../../../brand';
import { formatRelativeTimeAgo } from '../../../features/workflows/useAgenticRunDetail';
import { formatPassPercent } from '@chatsdet/types/agent-eval-result';
import { runVerdictPresentation } from '../../../features/agent-evals/agentEvalPresentation';
import { useAgentEvals, type AgentEvalListItem } from '../../../features/agent-evals/useAgentEvals';
import { FeatureOnboardingEmptyState } from '../../components/FeatureOnboardingEmptyState';

const COLUMNS: ColumnDef<StockFeatures, AgentEvalListItem>[] = [
  {
    id: 'name',
    header: 'Name',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="min-w-0">
        <Text size="sm" className="font-medium truncate">{row.original.name}</Text>
        <Text size="xs" className="text-muted-foreground truncate">{row.original.description}</Text>
      </div>
    ),
    meta: { flex: 3, minWidth: 220 },
  },
  {
    id: 'model',
    header: 'Agent default model',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm">{row.original.defaultModel}</Text>,
    meta: { flex: 1, minWidth: 140 },
  },
  {
    id: 'cases',
    header: 'Cases',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className="tabular-nums">{row.original.caseCount}</Text>,
    meta: { minWidth: 80 },
  },
  {
    id: 'verdict',
    header: 'Latest verdict',
    enableSorting: false,
    cell: ({ row }) => {
      if (!row.original.latestVerdict) return <Text size="sm" className="text-muted-foreground">No runs</Text>;
      const { label, color } = runVerdictPresentation({ verdict: row.original.latestVerdict, status: 'completed' });
      return <TagBadge name={label} color={color} size="md" />;
    },
    meta: { minWidth: 130 },
  },
  {
    id: 'score',
    header: 'Score vs bar',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.latestPassRate === null || row.original.latestPassBar === null ? (
        <Text size="sm" className="text-muted-foreground">—</Text>
      ) : (
        <Text size="sm" className="tabular-nums">
          {`${formatPassPercent(row.original.latestPassRate)} / ${formatPassPercent(row.original.latestPassBar)} bar`}
        </Text>
      ),
    meta: { minWidth: 150 },
  },
  {
    id: 'lastRan',
    header: 'Last ran',
    enableSorting: false,
    cell: ({ row }) => (
      <Text size="sm" className="text-muted-foreground">
        {row.original.latestRanAt ? formatRelativeTimeAgo(row.original.latestRanAt) : '—'}
      </Text>
    ),
    meta: { minWidth: 110 },
  },
];

const ONBOARDING_PROMPT = `Use the ${BRAND.name} CLI to set up an eval for an AI agent this app ships. First run \`${BRAND.cliCommand} eval guide\` and follow it. Then write a first eval with a few cases and run it once to record a pass rate.`;
const ONBOARDING_STEPS = [
  `Run \`${BRAND.cliCommand} eval guide\` for the setup guide.`,
  'Write an eval: a set of cases and graders for your agent.',
  'Run it at a commit to get a pass rate, cost and time per model.',
  'Evals and their latest results are listed here.',
];

export function AgentEvalsPage() {
  const evals = useAgentEvals();
  const items = evals.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <Page>
      <PageHeader
        icon={<Robot size={24} weight="fill" />}
        title="Evals"
        description="Evals that measure the AI agents your product ships, with their latest results."
      />
      {evals.isError && items.length === 0 ? (
        <Banner variant="error">{evals.error.message}</Banner>
      ) : evals.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      ) : items.length === 0 ? (
        <FeatureOnboardingEmptyState
          icon={<Robot size={48} />}
          graphic={<EmptyStateSpeedGraphic />}
          title="Measure your AI agents"
          whatItIs="An eval measures an AI agent your product ships: it runs the agent against a set of cases at a commit, grades every attempt, and reports a pass rate, cost and time."
          steps={ONBOARDING_STEPS}
          agentPrompt={ONBOARDING_PROMPT}
        />
      ) : (
        <>
          {evals.isError && <Banner variant="error">{evals.error.message}</Banner>}
          <DataTable
            columns={COLUMNS}
            data={items}
            aria-label="Evals table"
            getRowId={(row) => row.name}
            getRowHref={(row) => `/agent-evals/${encodeURIComponent(row.name)}`}
          />
          {evals.hasNextPage && (
            <div className="flex justify-center py-4">
              <Button variant="outline" onPress={() => evals.fetchNextPage()} isDisabled={evals.isFetchingNextPage}>
                {evals.isFetchingNextPage ? 'Loading...' : 'Load more'}
              </Button>
            </div>
          )}
        </>
      )}
    </Page>
  );
}
